import type { IInvestorRepository } from "@/application/ports/repositories";
import type {
  CompanyInvestor,
  CreateCompanyInvestorRequest,
  CreateIndividualInvestorRequest,
  IndividualInvestor,
  InvestorListParams,
  InvestorExportParams,
  InvestorDocumentType,
  InvestorListResponse,
  InvestorMetrics,
  InvestorMusicDeductionSummary,
  InvestorOperationResponse,
  InvestorStatus,
  InvestorType,
  UpdateCompanyInvestorRequest,
  UpdateIndividualInvestorRequest,
} from "@/domain/investor";
import type { OrganizationInvitation } from "@/domain/invitation";
import type {
  DocumentWithReviewResponse,
  DocumentDownloadResponse,
  InvestorPortfolioResponse,
  ReviewUpdateRequest,
  DocumentUpdateRequest,
} from "@/domain/documentBlocks";
import type { ExportDownloadOptions, ExportPreview } from "@/domain/export";
import { downloadBlob } from "../fileDownload";
import { apiClient, ApiClientError } from "../client";
import { buildExportPreviewUrl, buildExportDownloadUrl } from "../exportUrls";

export class InvestorRepository implements IInvestorRepository {
  async list(params: InvestorListParams = {}): Promise<InvestorListResponse> {
    const searchParams = new URLSearchParams();
    if (params.status) searchParams.append("status", params.status);
    if (params.investor_type) searchParams.append("investor_type", params.investor_type);
    if (params.search) searchParams.append("search", params.search);
    if (params.uuid) searchParams.append("uuid", params.uuid);
    if (params.sort_by) searchParams.append("sort_by", params.sort_by);
    if (params.sort_direction) searchParams.append("sort_direction", params.sort_direction);
    if (params.limit) searchParams.append("limit", params.limit.toString());
    if (params.offset) searchParams.append("offset", params.offset.toString());

    const response = await apiClient.get<InvestorListResponse>(
      `/investors?${searchParams.toString()}`,
    );
    return response.data;
  }

  async getMetrics(params?: {
    status?: InvestorStatus;
    investor_type?: InvestorType;
    search?: string;
  }): Promise<InvestorMetrics> {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.append("status", params.status);
    if (params?.investor_type) searchParams.append("investor_type", params.investor_type);
    if (params?.search) searchParams.append("search", params.search);

    const query = searchParams.toString();
    const url = query ? `/investors/metrics?${query}` : "/investors/metrics";
    const response = await apiClient.get<InvestorMetrics>(url);
    return response.data;
  }

  async getMusicDeductionSummary(
    uuid: string,
    fiscalYear: number,
  ): Promise<InvestorMusicDeductionSummary> {
    const response = await apiClient.get<InvestorMusicDeductionSummary>(
      `/investors/${uuid}/music-deduction-summary?fiscal_year=${fiscalYear}`,
    );
    return response.data;
  }

  async createIndividual(data: CreateIndividualInvestorRequest): Promise<IndividualInvestor> {
    const response = await apiClient.post<IndividualInvestor>("/investors/individuals", data);
    return response.data;
  }

  async getIndividual(uuid: string): Promise<IndividualInvestor> {
    const response = await apiClient.get<IndividualInvestor>(`/investors/individuals/${uuid}`);
    return response.data;
  }

  async updateIndividual(
    uuid: string,
    data: UpdateIndividualInvestorRequest,
  ): Promise<IndividualInvestor> {
    const response = await apiClient.patch<IndividualInvestor>(
      `/investors/individuals/${uuid}`,
      data,
    );
    return response.data;
  }

  async createCompany(data: CreateCompanyInvestorRequest): Promise<CompanyInvestor> {
    const response = await apiClient.post<CompanyInvestor>("/investors/companies", data);
    return response.data;
  }

  async getCompany(uuid: string): Promise<CompanyInvestor> {
    const response = await apiClient.get<CompanyInvestor>(`/investors/companies/${uuid}`);
    return response.data;
  }

  async updateCompany(uuid: string, data: UpdateCompanyInvestorRequest): Promise<CompanyInvestor> {
    const response = await apiClient.patch<CompanyInvestor>(`/investors/companies/${uuid}`, data);
    return response.data;
  }

  async delete(uuid: string): Promise<void> {
    await apiClient.delete(`/investors/${uuid}`);
  }

  async activate(uuid: string): Promise<InvestorOperationResponse> {
    const response = await apiClient.patch<InvestorOperationResponse>(
      `/investors/${uuid}/activate`,
    );
    return response.data;
  }

  async deactivate(uuid: string): Promise<InvestorOperationResponse> {
    const response = await apiClient.patch<InvestorOperationResponse>(
      `/investors/${uuid}/deactivate`,
    );
    return response.data;
  }

  async inviteToHub(uuid: string, lang: string = "es"): Promise<OrganizationInvitation> {
    const response = await apiClient.post<OrganizationInvitation>(
      `/investors/${uuid}/invite?lang=${encodeURIComponent(lang)}`,
    );
    return response.data;
  }

  async listDocuments(
    investorUuid: string,
    params?: {
      doc_type?: string;
      status?: string;
      fiscal_quota_uuid?: string;
      search?: string;
      limit?: number;
      offset?: number;
    },
  ): Promise<InvestorPortfolioResponse> {
    const searchParams = new URLSearchParams();
    if (params?.doc_type) searchParams.append("doc_type", params.doc_type);
    if (params?.status) searchParams.append("status", params.status);
    if (params?.fiscal_quota_uuid)
      searchParams.append("fiscal_quota_uuid", params.fiscal_quota_uuid);
    if (params?.search) searchParams.append("search", params.search);
    if (params?.limit) searchParams.append("limit", params.limit.toString());
    if (params?.offset) searchParams.append("offset", params.offset.toString());

    const query = searchParams.toString();
    const url = query
      ? `/investors/${investorUuid}/documents?${query}`
      : `/investors/${investorUuid}/documents`;
    const response = await apiClient.get<InvestorPortfolioResponse>(url);
    return response.data;
  }

  async uploadDocument(
    investorUuid: string,
    file: File,
    docType: InvestorDocumentType,
    title?: string,
  ): Promise<DocumentWithReviewResponse> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("doc_type", docType);
    if (title) formData.append("title", title);

    const response = await apiClient.post<DocumentWithReviewResponse>(
      `/investors/${investorUuid}/documents`,
      formData,
    );
    return response.data;
  }

  async getDocument(
    investorUuid: string,
    documentUuid: string,
  ): Promise<DocumentWithReviewResponse> {
    const response = await apiClient.get<DocumentWithReviewResponse>(
      `/investors/${investorUuid}/documents/${documentUuid}`,
    );
    return response.data;
  }

  async getDocumentUrl(
    investorUuid: string,
    documentUuid: string,
  ): Promise<DocumentDownloadResponse> {
    const response = await apiClient.get<DocumentDownloadResponse>(
      `/investors/${investorUuid}/documents/${documentUuid}/download`,
    );
    return response.data;
  }

  async downloadDocument(
    investorUuid: string,
    documentUuid: string,
    fileName?: string,
  ): Promise<void> {
    const documentResponse = await this.getDocumentUrl(investorUuid, documentUuid);

    const actualFileName = documentResponse.filename || fileName || `document-${documentUuid}.pdf`;

    const response = await fetch(documentResponse.signed_url, {
      method: "GET",
      mode: "cors",
      credentials: "omit",
      headers: { Accept: "application/pdf" },
    });

    if (!response.ok) {
      throw new ApiClientError({
        message: `Failed to download document: ${response.status} ${response.statusText}`,
        code: "DOWNLOAD_ERROR",
        status: response.status,
      });
    }

    const blob = await response.blob();
    downloadBlob(blob, actualFileName);
  }

  async deleteDocument(investorUuid: string, documentUuid: string): Promise<void> {
    await apiClient.delete(`/investors/${investorUuid}/documents/${documentUuid}`);
  }

  async reviewDocument(
    investorUuid: string,
    documentUuid: string,
    review: ReviewUpdateRequest,
  ): Promise<DocumentWithReviewResponse> {
    const response = await apiClient.patch<DocumentWithReviewResponse>(
      `/investors/${investorUuid}/documents/${documentUuid}/review`,
      review,
    );
    return response.data;
  }

  async updateDocument(
    investorUuid: string,
    documentUuid: string,
    data: DocumentUpdateRequest,
  ): Promise<DocumentWithReviewResponse> {
    const response = await apiClient.patch<DocumentWithReviewResponse>(
      `/investors/${investorUuid}/documents/${documentUuid}`,
      data,
    );
    return response.data;
  }

  async previewExport(params: InvestorExportParams): Promise<ExportPreview> {
    const url = buildExportPreviewUrl("/investors", investorExportParamsToQuery(params));
    const response = await apiClient.get<ExportPreview>(url);
    return response.data;
  }

  async downloadExport(
    params: InvestorExportParams,
    options: ExportDownloadOptions,
  ): Promise<void> {
    const url = buildExportDownloadUrl("/investors", investorExportParamsToQuery(params), options);
    const { blob, filename } = await apiClient.getBlobWithFilename(
      url,
      `investors.${options.format}`,
    );
    downloadBlob(blob, filename);
  }
}

function investorExportParamsToQuery(params: InvestorExportParams): URLSearchParams {
  const query = new URLSearchParams();
  if (params.search) query.append("search", params.search);
  if (params.status) query.append("status", params.status);
  if (params.investor_type) query.append("investor_type", params.investor_type);
  if (params.sort_by) query.append("sort_by", params.sort_by);
  if (params.sort_direction) query.append("sort_direction", params.sort_direction);
  return query;
}
